import { externalLinksSchema } from "./externalLinks.schema";

export const createUserSchema = {
  type: "object",
  properties: {
    firstName: { type: "string", minLength: 1 },
    lastName: { type: "string", minLength: 1 },
    email: { type: "string", minLength: 1 },
    role: { type: "string", enum: ["sysAdmin", "wsAdmin", "member"] },
    // sysAdmin-only: sets the user's password directly instead of going
    // through the invite/set-password flow. Enforced in the service, not
    // here — the schema only needs to keep AJV's removeAdditional from
    // stripping the field.
    password: { type: "string", minLength: 8 },
  },
  required: ["firstName", "lastName", "email", "role"],
};

// `config` is a freeform JSON object (theme, language, mobileUI,
// remindBeforeSession, isTicketAdmin, etc.) merged in the service, so it is
// declared as a permissive object. The privileged `isTicketAdmin` flag inside
// config is stripped for non-sysAdmins by the service guard, not the schema.
const userConfigSchema = { type: "object", additionalProperties: true };

// Self-service profile-edit surface (PATCH /user/self). Each surface declares
// only the fields it actually accepts: with `additionalProperties: false` +
// AJV removeAdditional, anything not listed here is silently stripped at the
// edge, so this list must cover every field web/mobile PATCH to /user/self
// (including country/isSetupComplete, which the admin surface does not use).
// role is intentionally absent so a member-reachable surface cannot carry a
// role change. The authoritative RBAC guard still lives in
// UserService.patchUser (Repository.update has no column whitelist); the schema
// is only defense in depth.
export const updateUserSchema = {
  type: "object",
  properties: {
    picture: { type: ["string", "null"] },
    firstName: { type: "string", minLength: 1 },
    lastName: { type: "string", minLength: 1 },
    email: { type: "string", format: "email", minLength: 1 },
    occupation: { type: ["string", "null"] },
    biography: { type: ["string", "null"], maxLength: 1000 },
    country: { type: ["string", "null"] },
    discoverable: { type: "boolean" },
    isDataStorageConsentGiven: { type: "boolean" },
    isSetupComplete: { type: "boolean" },
    links: { type: "array", items: externalLinksSchema },
    config: userConfigSchema,
  },
  additionalProperties: false,
};

// Admin update surface (PATCH /users/:id). Mirrors the fields the web admin
// EditUser form sends and additionally allows role. role is only honored when
// the caller is a sysAdmin; UserService.patchUser strips it for everyone else,
// so exposing it here is safe. country/isSetupComplete are intentionally NOT
// listed — they are self-service-only concerns and are stripped on this surface.
export const adminUpdateUserSchema = {
  type: "object",
  properties: {
    picture: { type: ["string", "null"] },
    firstName: { type: "string", minLength: 1 },
    lastName: { type: "string", minLength: 1 },
    email: { type: "string", format: "email", minLength: 1 },
    role: { type: "string", enum: ["sysAdmin", "wsAdmin", "member"] },
    occupation: { type: ["string", "null"] },
    biography: { type: ["string", "null"], maxLength: 1000 },
    discoverable: { type: "boolean" },
    isDataStorageConsentGiven: { type: "boolean" },
    links: { type: "array", items: externalLinksSchema },
    config: userConfigSchema,
  },
  additionalProperties: false,
};

export const web_createUserSchema = {
  type: "object",
  properties: {
    firstName: { type: "string", minLength: 1 },
    lastName: { type: "string", minLength: 1 },
    email: { type: "string", format: "email", minLength: 1 },
    role: { type: "string", enum: ["sysAdmin", "wsAdmin", "member"] },
    config: {
      type: "object",
      properties: {
        isTicketAdmin: { type: "boolean" },
      },
    },
  },
  required: ["firstName", "lastName", "email", "role"],
};

export const web_updateUserSchema = {
  type: "object",
  properties: {
    firstName: { type: "string", minLength: 1 },
    lastName: { type: "string", minLength: 1 },
    email: { type: "string", format: "email", minLength: 1 },
    occupation: { type: ["string", "null"], maxLength: 100 },
    biography: { type: ["string", "null"], maxLength: 1000 },
    discoverable: { type: "boolean" },
    picture: { type: ["string", "null"] },
    isDataStorageConsentGiven: { type: "boolean" },
  },
};

// mobile-next's Edit Profile screen — a narrower self-service surface than
// `mobile_editProfileSchema` (the previous app's version, which also carries
// email/country): this screen only edits what the profile page actually
// renders (avatar, name, occupation, bio) plus the discoverable/consent
// settings carried over from that same screen.
export const mobileNext_editProfileSchema = {
  type: "object",
  properties: {
    picture: { type: ["string", "null"] },
    firstName: { type: "string", minLength: 1, maxLength: 50 },
    lastName: { type: "string", minLength: 1, maxLength: 50 },
    occupation: { type: ["string", "null"], maxLength: 100 },
    biography: { type: ["string", "null"], maxLength: 1000 },
    discoverable: { type: "boolean" },
    isDataStorageConsentGiven: {
      type: "boolean",
      const: true,
      errorMessage: {
        const:
          "You must consent to the processing of your profile data in order to save changes!",
      },
    },
  },
  required: ["firstName", "lastName"],
  additionalProperties: false,
};

export const getUserEventsSchema = {
  querystring: {
    type: "object",
    properties: {
      status: { type: "string", enum: ["draft", "active", "closed"] },
    },
  },
};

export const deleteUserSchema = {
  params: {
    type: "object",
    properties: { id: { type: "integer" } },
    required: ["id"],
  },
};

export const userPublicSchema = {
  params: {
    type: "object",
    properties: {
      userId: { type: "integer" },
    },
    required: ["userId"],
  },
};

export const userBookmarkedSessionsSchema = {
  params: {
    type: "object",
    properties: {
      userId: { type: "integer" },
    },
    required: ["userId"],
  },
  querystring: {
    type: "object",
    properties: {
      eventId: { type: "integer" },
    },
    required: ["eventId"],
  },
};

export const web_updateProfileSchema = {
  type: "object",
  properties: {
    firstName: {
      type: "string",
      minLength: 1,
      errorMessage: {
        minLength: "First name is required",
      },
    },
    lastName: {
      type: "string",
      minLength: 1,
      errorMessage: {
        minLength: "Last name is required",
      },
    },
    email: {
      type: "string",
      pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
      minLength: 1,
      errorMessage: {
        pattern: "Invalid email address",
        minLength: "Email is required",
      },
    },
    occupation: {
      type: "string",
      maxLength: 100,
      errorMessage: {
        maxLength: "Occupation must be 100 characters or less",
      },
    },
    biography: {
      type: "string",
      maxLength: 1000,
      errorMessage: {
        maxLength: "Biography must be 1000 characters or less",
      },
    },
    discoverable: {
      type: "boolean",
    },
    currentEventId: {
      type: ["string", "null"],
    },
    picture: {
      type: ["string", "null"],
    },
    isDataStorageConsentGiven: {
      type: "boolean",
      const: true,
      errorMessage: {
        const:
          "You must consent to the processing of your profile data in order to save changes!",
      },
    },
    currentWorkspaceId: {
      type: "string",
      errorMessage: {
        type: "Workspace ID must be a string",
      },
    },
  },
  required: [
    "firstName",
    "lastName",
    "email",
    "occupation",
    "biography",
    "discoverable",
    "isDataStorageConsentGiven",
  ],
  additionalProperties: false,
};

export const mobile_editProfileSchema = {
  type: "object",
  properties: {
    // TODO: We need to implement a validation for the image, string is not enough
    // We need to sure that the picture is base64 with image jpg or png
    picture: { type: ["string", "null"] },
    firstName: { type: "string", minLength: 1, maxLength: 20 },
    lastName: { type: "string", minLength: 1, maxLength: 20 },
    email: { type: "string", minLength: 1, maxLength: 50 },
    occupation: { type: ["string", "null"], maxLength: 50 },
    biography: { type: ["string", "null"], maxLength: 1000 },
    country: { type: ["string", "null"] },
    discoverable: { type: "boolean" },
    isDataStorageConsentGiven: { type: "boolean" },
  },
  required: ["firstName", "lastName", "email", "discoverable"],
  additionalProperties: false,
};
